// OGC-531 shared dropdown popup behaviour for Select (listbox) and Menu
// (menu). The panel is portaled out of clipping table/card containers, into
// the nearest dialog when the trigger lives in one (a body portal would be
// inert or "outside" a modal), and shown in the top layer with the Popover
// API where available so modal dialogs and overflow never clip it. Only one
// dropdown is open at a time across the app.

export const DROPDOWN_VIEWPORT_PADDING = 8;
export const DROPDOWN_GAP = 6;
export const DROPDOWN_MAX_HEIGHT = 320;
export const DROPDOWN_MIN_WIDTH = 224;
export const DROPDOWN_MIN_HEIGHT = 44;
export const TYPE_AHEAD_RESET_MS = 700;

const DIALOG_CONTAINER_SELECTOR = 'dialog[open], [role="dialog"], [role="alertdialog"]';

// Pure placement: below the trigger unless there is more room above and the
// panel does not fit below; Select aligns to the trigger's left edge, Menu to
// its right edge; always inside the viewport padding.
export function computeDropdownPlacement({
  triggerRect,
  panelHeight,
  viewportWidth,
  viewportHeight,
  kind = 'select',
  minWidth = DROPDOWN_MIN_WIDTH,
}) {
  const padding = DROPDOWN_VIEWPORT_PADDING;
  const usableWidth = Math.max(0, viewportWidth - padding * 2);
  const width = Math.min(Math.max(triggerRect.width, minWidth), usableWidth);
  const below = viewportHeight - triggerRect.bottom - DROPDOWN_GAP - padding;
  const above = triggerRect.top - DROPDOWN_GAP - padding;
  const placeAbove = below < panelHeight && above > below;
  const available = Math.max(DROPDOWN_MIN_HEIGHT, placeAbove ? above : below);
  const maxHeight = Math.min(panelHeight, available, DROPDOWN_MAX_HEIGHT);
  const anchorLeft = kind === 'menu' ? triggerRect.right - width : triggerRect.left;
  const left = Math.max(padding, Math.min(anchorLeft, viewportWidth - width - padding));
  const top = Math.max(
    padding,
    placeAbove
      ? triggerRect.top - DROPDOWN_GAP - maxHeight
      : Math.min(triggerRect.bottom + DROPDOWN_GAP, viewportHeight - maxHeight - padding),
  );
  return { left, top, width, maxHeight, placement: placeAbove ? 'above' : 'below' };
}

// Pure type-ahead: repeated presses of one letter cycle through matches from
// the item after the active one; a typed prefix searches from the active item.
// Returns the new buffer and the matching index (or -1).
export function typeAheadMatch({ labels, activeIndex, buffer = '', lastTypedAt = 0, character, now }) {
  const lower = String(character || '').toLocaleLowerCase();
  const typed = now - lastTypedAt > TYPE_AHEAD_RESET_MS ? lower : buffer + lower;
  const repeated = [...typed].every((letter) => letter === typed[0]);
  const search = repeated ? typed[0] : typed;
  const start = repeated ? activeIndex + 1 : activeIndex;
  const count = labels.length;
  for (let offset = 0; offset < count; offset += 1) {
    const index = (((start + offset) % count) + count) % count;
    if (String(labels[index] || '').trim().toLocaleLowerCase().startsWith(search)) {
      return { buffer: typed, index };
    }
  }
  return { buffer: typed, index: -1 };
}

export function dropdownPortalTarget(trigger) {
  if (typeof document === 'undefined') return null;
  return trigger?.closest?.(DIALOG_CONTAINER_SELECTOR) || document.body;
}

export function showInTopLayer(panel) {
  if (!panel || typeof panel.showPopover !== 'function') return false;
  try {
    if (!panel.matches(':popover-open')) panel.showPopover();
    return true;
  } catch {
    return false;
  }
}

export function positionDropdown(panel, trigger, kind) {
  if (!panel || !trigger || typeof window === 'undefined') return;
  const triggerRect = trigger.getBoundingClientRect();
  const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
  const viewportHeight = window.innerHeight;
  Object.assign(panel.style, {
    position: 'fixed',
    margin: '0',
    inset: 'auto',
    width: `${Math.min(Math.max(triggerRect.width, DROPDOWN_MIN_WIDTH), viewportWidth - DROPDOWN_VIEWPORT_PADDING * 2)}px`,
    maxHeight: 'none',
    visibility: 'hidden',
  });
  // Measure the uncapped border-box height. scrollHeight omits the borders,
  // so a max-height built from it left the content 2px short and every list
  // scrolled; the cap now applies only when the list needs it.
  const naturalHeight = Math.ceil(panel.getBoundingClientRect().height);
  const placement = computeDropdownPlacement({
    triggerRect,
    panelHeight: naturalHeight,
    viewportWidth,
    viewportHeight,
    kind,
  });
  Object.assign(panel.style, {
    left: `${placement.left}px`,
    top: `${placement.top}px`,
    width: `${placement.width}px`,
    maxHeight: `${placement.maxHeight}px`,
    visibility: '',
  });
  panel.dataset.placement = placement.placement;
}

let openDropdownClose = null;

// Registers the dropdown being opened and closes any other open one. Returns
// a release function the dropdown calls when it closes.
export function claimOpenDropdown(close) {
  if (openDropdownClose && openDropdownClose !== close) {
    const previous = openDropdownClose;
    openDropdownClose = null;
    previous({ restoreFocus: false });
  }
  openDropdownClose = close;
  return () => {
    if (openDropdownClose === close) openDropdownClose = null;
  };
}

export function focusWithoutScroll(element) {
  if (!element || element.isConnected === false || typeof element.focus !== 'function') return;
  try {
    element.focus({ preventScroll: true });
  } catch {
    element.focus();
  }
}

// True when the anchor moved more than a pixel from where the panel was
// placed. Scroll events are dispatched a frame late, so a scroll that brought
// the trigger into view just before it opened must not close the panel.
export function anchorMoved(initialRect, currentRect) {
  if (!initialRect || !currentRect) return true;
  return Math.abs(initialRect.top - currentRect.top) > 1 || Math.abs(initialRect.left - currentRect.left) > 1;
}

// Subscribes the dismissal rules every open dropdown shares: an outside
// pointerdown or focus closes without stealing focus; scrolling that moves
// the anchor, resizing or leaving the window closes. Returns an unsubscribe.
export function subscribeDropdownDismissal({ anchor, isInside, isInsidePanel, close }) {
  const initialRect = anchor?.getBoundingClientRect?.();
  function handlePointerDown(event) {
    if (!isInside(event.target)) close({ restoreFocus: false }, event);
  }
  function handleFocusIn(event) {
    if (!isInside(event.target)) close({ restoreFocus: false });
  }
  function handleScroll(event) {
    if (isInsidePanel(event.target)) return;
    if (!anchorMoved(initialRect, anchor?.getBoundingClientRect?.())) return;
    close({ restoreFocus: isInsidePanel(document.activeElement) });
  }
  function handleResize() {
    close({ restoreFocus: isInsidePanel(document.activeElement) });
  }
  function handleBlur() {
    close({ restoreFocus: false });
  }
  document.addEventListener('pointerdown', handlePointerDown, true);
  document.addEventListener('focusin', handleFocusIn);
  document.addEventListener('scroll', handleScroll, true);
  window.addEventListener('resize', handleResize);
  window.addEventListener('blur', handleBlur);
  return () => {
    document.removeEventListener('pointerdown', handlePointerDown, true);
    document.removeEventListener('focusin', handleFocusIn);
    document.removeEventListener('scroll', handleScroll, true);
    window.removeEventListener('resize', handleResize);
    window.removeEventListener('blur', handleBlur);
  };
}
